import { useEffect } from "react";
import TagManager from "react-gtm-module";
import SEO from "./SEO";
import styles from "./About.module.css";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "אודות ווטרלס – המפיצה הבלעדית של משתנות ללא מים בישראל",
  url: "https://waterless.co.il/about",
  description:
    "ווטרלס היא המפיצה הבלעדית בישראל של משתנות ללא מים מבית Falcon Waterfree Technologies. מעל 14 שנות ניסיון בישראל ו-35 שנות הצלחה בינלאומית בחיסכון מים וקיימות סביבתית.",
  mainEntity: {
    "@type": "Organization",
    name: "ווטרלס - Waterless",
    url: "https://waterless.co.il",
    logo: "https://waterless.co.il/assets/images/waterlessLogo.png",
    foundingDate: "2011",
    description:
      "המפיצה הבלעדית בישראל של משתנות ללא מים מבית Falcon Waterfree Technologies. פתרונות חסכוניים, היגייניים וידידותיים לסביבה לעסקים, מוסדות ציבור וארגונים.",
    address: {
      "@type": "PostalAddress",
      streetAddress: "צבי רוזנבלט 19",
      addressLocality: "תל אביב",
      postalCode: "68151",
      addressCountry: "IL",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+972-54-4494384",
      contactType: "customer service",
      availableLanguage: ["Hebrew", "English"],
    },
    email: "waterless.israel@gmail.com",
    areaServed: "IL",
    knowsAbout: [
      "משתנות ללא מים",
      "חיסכון במים",
      "קיימות סביבתית",
      "שירותים ציבוריים",
      "Falcon Waterfree Technologies",
    ],
  },
};

const videoSchema = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: "ההיסטוריה של Falcon Waterfree Technologies",
  description:
    "סרטון המספר את ההיסטוריה של Falcon Waterfree Technologies, החברה המובילה בעולם בפיתוח משתנות ללא מים. מעל 35 שנות חדשנות בחיסכון מים וקיימות.",
  thumbnailUrl: "https://waterless.co.il/assets/images/waterlessLogo.png",
  contentUrl: "https://falconwatertech.com/Falconhistory.mp4",
  uploadDate: "2023-01-01",
  inLanguage: "en",
};

function About(props) {
  useEffect(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: "pageview",
        pagePath: "/about",
        pageTitle: props.title,
      },
    });
    window.scrollTo(0, 0);
  }, [props.title]);

  return (
    <div className="About-section">
      <SEO
        title="אודות ווטרלס | המפיצה הבלעדית למשתנות ללא מים בישראל | 14 שנות ניסיון"
        description="ווטרלס – המפיצה הבלעדית בישראל של משתנות ללא מים מבית Falcon Waterfree Technologies. מעל 14 שנות ניסיון בישראל, 35 שנות הצלחה בינלאומית. פתרונות חסכוניים, היגייניים וירוקים לעסקים, קניונים, מוסדות חינוך, בתי מלון ורשויות מקומיות."
        path="/about"
        structuredData={[aboutSchema, videoSchema]}
      />

      {/* ===== Hero Section ===== */}
      <section className={styles.heroSection}>
        <div className="container-fluid px-3 px-md-5">
          <span className={styles.heroLabel}>אודות ווטרלס</span>
          <h1 className={styles.heroTitle}>
            המפיצה הבלעדית בישראל למשתנות ללא מים — חיסכון, היגיינה וקיימות
          </h1>
          <div className={styles.videoWrapper}>
            <video
              controls
              autoPlay
              muted
              playsInline
              aria-label="סרטון על ההיסטוריה של פלקון"
            >
              <source
                src="https://falconwatertech.com/Falconhistory.mp4"
                type="video/mp4"
              />
              הדפדפן שלך לא תומך בתג וידאו.
            </video>
          </div>
        </div>
      </section>

      {/* ===== Values Section ===== */}
      <section className={styles.valuesSection}>
        <div className="container-fluid px-3 px-md-5">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>הערכים והעקרונות שמנחים אותנו</h2>
            <p className={styles.sectionDescription}>
              אנחנו מאמינים שחיסכון במים ושמירה על הסביבה מתחילים בשירותים. כל
              משתנה ללא מים שאנו מתקינים חוסכת עד 100,000 ליטר מים בשנה.
            </p>
          </div>
          <div className={styles.valuesGrid}>
            <div className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <img
                  src="./assets/images/icons/ecology 1.png"
                  className={styles.valueIcon}
                  alt="משתנות ללא מים ידידותיות לסביבה - חיסכון במים וקיימות"
                  loading="lazy"
                />
              </div>
              <h3 className={styles.valueTitle}>קיימות סביבתית</h3>
              <p className={styles.valueDescription}>
                משתנות ללא מים מחימר איכותי — ללא הדחה, ללא ריח, ללא סתימות.
                פתרון ירוק שחוסך מים יקרים ומפחית את טביעת הרגל הסביבתית של
                הארגון שלכם.
              </p>
            </div>

            <div className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <img
                  src="./assets/images/icons/piggy-bank 1.png"
                  className={styles.valueIcon}
                  alt="חיסכון בעלויות מים ותחזוקה עם משתנות Waterless"
                  loading="lazy"
                />
              </div>
              <h3 className={styles.valueTitle}>חיסכון כלכלי משמעותי</h3>
              <p className={styles.valueDescription}>
                הפחתת עלויות מים, ביוב ותחזוקה שוטפת. משתנות ללא מים מצריכות
                תחזוקה מינימלית ומחזירות את ההשקעה תוך זמן קצר — ROI מוכח
                לעסקים ומוסדות.
              </p>
            </div>

            <div className={styles.valueCard}>
              <div className={styles.valueIconWrapper}>
                <img
                  src="./assets/images/icons/satisfaction 1.png"
                  className={styles.valueIcon}
                  alt="שירות לקוחות מקצועי - התקנה ותחזוקת משתנות ללא מים"
                  loading="lazy"
                />
              </div>
              <h3 className={styles.valueTitle}>שירות ומקצועיות</h3>
              <p className={styles.valueDescription}>
                מייעוץ ראשוני, דרך התקנה מקצועית ועד תחזוקה שוטפת — אנו מלווים
                את הלקוחות שלנו בכל שלב. שביעות רצון הלקוח היא העדיפות הראשונה
                שלנו.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Company Section ===== */}
      <section className={styles.companySection}>
        <div className="container-fluid px-3 px-md-5">
          <div className={styles.companyRow}>
            <div className={styles.companyTextCol}>
              <h2 className={styles.companyTitle}>
                ווטרלס — המפיצה הבלעדית של Falcon בישראל
              </h2>
              <p className={styles.companyDescription}>
                ווטרלס היא המפיצה הרשמית והבלעדית בישראל של מוצרי Falcon
                Waterfree Technologies — המותג מספר 1 בעולם למשתנות ללא מים, עם
                מעל 35 שנות חדשנות ומאות אלפי התקנות בכל היבשות. אנו משרתים לקוחות
                עסקיים ומוסדיים בכל רחבי הארץ: רשויות מקומיות, קניונים, אולמות
                אירועים, מתקני ספורט, בתי מלון, מוסדות חינוך, התעשייה האווירית
                ועוד. כל משתנה שאנו מתקינים פועלת על עקרון הכבידה בלבד — ללא מים,
                ללא חשמל וללא כימיקלים מזיקים — ולכן היא גם כשרה לשבת וגם ידידותית
                לסביבה. מעבר לחיסכון הכלכלי, השימוש במשתנות ללא מים מהווה כלי
                חינוכי חשוב להעלאת המודעות לשמירה על מקורות המים בישראל.
              </p>
              <div className={styles.statsRow}>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>14+</div>
                  <p className={styles.statLabel}>שנות ניסיון בישראל</p>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statValue}>35+</div>
                  <p className={styles.statLabel}>שנות הצלחה בינלאומית</p>
                </div>
              </div>
            </div>
            <div className={styles.companyImageCol}>
              <div className={styles.companyImageWrapper}>
                <img
                  src="./assets/images/Rectangle 29.png"
                  alt="צוות חברת ווטרלס - המפיצה הבלעדית של משתנות ללא מים בישראל"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Why Choose Us Section ===== */}
      <section className={styles.whyUsSection}>
        <div className="container-fluid px-3 px-md-5">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>למה לבחור בווטרלס</h2>
            <p className={styles.sectionDescription}>
              לא רק המוצר המוביל בעולם — אלא גם הגיבוי, השירות והאחריות שמגיעים
              איתו. אלה הדברים שהופכים אותנו לבחירה הבטוחה בישראל.
            </p>
          </div>
          <div className={styles.whyUsGrid}>
            <div className={styles.whyUsCard}>
              <span className={`iconify ${styles.whyUsIcon}`} data-icon="mdi:earth" aria-hidden="true"></span>
              <h3 className={styles.whyUsTitle}>המותג מספר 1 בעולם</h3>
              <p className={styles.whyUsDesc}>
                Falcon Waterfree Technologies — מאות אלפי התקנות ברחבי העולם, מנמלי
                תעופה ואצטדיונים ועד מגדלי משרדים. טכנולוגיה מוכחת, לא ניסוי.
              </p>
            </div>
            <div className={styles.whyUsCard}>
              <span className={`iconify ${styles.whyUsIcon}`} data-icon="mdi:candle" aria-hidden="true"></span>
              <h3 className={styles.whyUsTitle}>כשר לשבת</h3>
              <p className={styles.whyUsDesc}>
                פעולה מכנית בלבד, ללא חשמל וללא חיישנים — מותר לשימוש בשבת ובחג.
                פתרון אידיאלי למוסדות, בתי כנסת, מלונות ועסקים שומרי מסורת.
              </p>
            </div>
            <div className={styles.whyUsCard}>
              <span className={`iconify ${styles.whyUsIcon}`} data-icon="mdi:leaf" aria-hidden="true"></span>
              <h3 className={styles.whyUsTitle}>ללא כימיקלים מזיקים</h3>
              <p className={styles.whyUsDesc}>
                נוזל איטום ביולוגי מתכלה וידידותי לסביבה — חוסם ריחות בצורה טבעית,
                ללא חומצות וללא חומרים רעילים שפוגעים בצנרת או בסביבה.
              </p>
            </div>
            <div className={styles.whyUsCard}>
              <span className={`iconify ${styles.whyUsIcon}`} data-icon="mdi:shield-check" aria-hidden="true"></span>
              <h3 className={styles.whyUsTitle}>אחריות ושירות ארצי</h3>
              <p className={styles.whyUsDesc}>
                אחריות יצרן מלאה, אספקת חלקים שוטפת ותחזוקה בכל רחבי הארץ. מוצר
                עולמי עם גיבוי מקומי — אתם אף פעם לא לבד.
              </p>
            </div>
            <div className={styles.whyUsCard}>
              <span className={`iconify ${styles.whyUsIcon}`} data-icon="mdi:certificate" aria-hidden="true"></span>
              <h3 className={styles.whyUsTitle}>עמידה בתקנים ירוקים</h3>
              <p className={styles.whyUsDesc}>
                מסייעים לעמוד בתקן הבנייה הירוקה הישראלי 5281 ובתקן הבינלאומי LEED,
                עם ניקוד משמעותי בקטגוריית חיסכון במים.
              </p>
            </div>
            <div className={styles.whyUsCard}>
              <span className={`iconify ${styles.whyUsIcon}`} data-icon="mdi:handshake-outline" aria-hidden="true"></span>
              <h3 className={styles.whyUsTitle}>14+ שנות ניסיון בישראל</h3>
              <p className={styles.whyUsDesc}>
                ליווי מקצועי מהייעוץ הראשוני ועד התחזוקה השוטפת, עם היכרות עמוקה של
                הצרכים והתקנות בשוק הישראלי.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Installations Section ===== */}
      <section className={styles.installationsSection}>
        <div className="container-fluid px-3 px-md-5">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>מהתקנות אצל לקוחותינו</h2>
            <p className={styles.sectionDescription}>
              הצצה למתחמים שבחרו לחסוך מים עם משתנות ללא מים של ווטרלס — קניונים,
              מלונות, משרדים ומוסדות ציבור ברחבי הארץ.
            </p>
          </div>
          <div className={styles.installationsGrid}>
            <div className={styles.installationItem}>
              <img
                src="./assets/images/gallery/installation-8.png"
                alt="משתנות ללא מים בשירותים ציבוריים גדולים"
                loading="lazy"
              />
            </div>
            <div className={styles.installationItem}>
              <img
                src="./assets/images/gallery/installation-2.png"
                alt="משתנות ללא מים עם מחיצות זכוכית במסעדה"
                loading="lazy"
              />
            </div>
            <div className={styles.installationItem}>
              <img
                src="./assets/images/gallery/installation-4.png"
                alt="משתנות ללא מים כהות במלון בוטיק"
                loading="lazy"
              />
            </div>
            <div className={styles.installationItem}>
              <img
                src="./assets/images/gallery/installation-12.png"
                alt="משתנות ללא מים עם קיר דקורטיבי"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Partners Section ===== */}
      <section className={styles.partnersSection}>
        <div className="container-fluid px-3 px-md-5">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>השותפים והלקוחות שלנו</h2>
            <p className={styles.sectionDescription}>
              אנו גאים לעבוד עם ארגונים ומוסדות מובילים בישראל שבחרו לחסוך מים
              ולשמור על הסביבה באמצעות משתנות ללא מים.
            </p>
          </div>
          <div className={styles.partnersGrid}>
            <div className={styles.partnerLogo}>
              <img
                src="./assets/images/1027442.png"
                alt="לוגו שותף עסקי"
                loading="lazy"
              />
            </div>
            <div className={styles.partnerLogo}>
              <img
                src="./assets/images/1028945 1.png"
                alt="לוגו שותף עסקי"
                loading="lazy"
              />
            </div>
            <div className={styles.partnerLogo}>
              <img
                src="./assets/images/1028942.png"
                alt="לוגו שותף עסקי"
                loading="lazy"
              />
            </div>
            <div className={styles.partnerLogo}>
              <img
                src="./assets/images/1028928.png"
                alt="לוגו שותף עסקי"
                loading="lazy"
              />
            </div>
            <div className={styles.partnerLogo}>
              <img
                src="./assets/images/1028941.png"
                alt="לוגו שותף עסקי"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
